// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import {formatPercent as formatPercentCore} from 'common/util';
import {PropertyType, ResourceType} from 'common/report/constants';
import {OTHER_PROP, OVERALL_PROP, SEGMENT_SEPARATOR} from 'common/report/funnels/models/constants';
import {
  BasicFunnelStep,
  ChartType,
  FunnelBookmark,
  FunnelSegment,
  FunnelStep,
  isOrStep,
} from 'common/report/funnels/models/types';
import {propertiesUtils, renamePropertyOld} from 'common/report/util/properties';
import {
  getFirstSegment,
  getSegmentPropertyType,
  isCohortSegment,
  isEventSegment,
  isUserSegment,
  OtherSegmentation,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {CommonDataTableFields, SegmentColumnSpec} from 'common/components/data-table/types';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {getDefaultSortConfig} from 'common/report/util/sorting/table-sorting';
import {FlatValueSortConfig, FunnelOrder, SortBy, SortConfig, SortOrder} from 'common/types/reports/bookmark';
import {pluralize} from 'common/util/string';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {LocalizationStore} from 'common/i18n/store';

export function formatPercent(decimal): string {
  return formatPercentCore(decimal, {
    precision: 1,
    showSign: true,
    exactPrecision: false,
  });
}

/**
 * convert a raw string to an enum value if it exists, else return a default
 * @param value raw string value to convert
 * @param enumMap enum type
 * @param defaultMember default instance of enum
 */
export function fromEnum<T>(value: string, enumType: T, defaultMember: T[keyof T]): T[keyof T] {
  if (Object.values(enumType).includes(value)) {
    return value as unknown as T[keyof T];
  }
  return defaultMember;
}

export function convertSegmentLabelForCohort(
  label: string,
  segmentNames: Array<string | string[]>,
  transform: (data: {title: string; value: string}) => string = ({title, value}) => `${title}: ${value}`,
): string {
  return label
    .split(SEGMENT_SEPARATOR)
    .map((segmentValue, idx) => ({
      value: segmentValue,
      title: renamePropertyOld(segmentNames[idx]),
    }))
    .map(transform)
    .join(SEGMENT_SEPARATOR);
}

function getDefaultFunnelTrendSortConfig(bookmarkParams: FunnelBookmark, i18n: LocalizationStore) {
  const numSegmentColumns = getSegmentColumnSpecs(bookmarkParams, i18n).length || 1;
  return getDefaultSortConfig({
    numSegmentColumns,
    valueSortField: CommonDataTableFields.CohortSize,
    allowGroupSort: true,
  });
}

export const DEFAULT_STEP_SORT_FIELD = `step-0`;
function getDefaultFunnelStepsSortConfig(numSegmentColumns: number) {
  return getDefaultSortConfig({
    numSegmentColumns,
    valueSortField: DEFAULT_STEP_SORT_FIELD,
    allowGroupSort: true,
  });
}

/** Converts from the old sorting config format or gets the default for funnel steps */
function convertOrDefaultFunnelStepsSortConfig(bookmarkParams: FunnelBookmark, i18n: LocalizationStore) {
  const numSegmentColumns = getSegmentColumnSpecs(bookmarkParams, i18n).length || 1;
  let sorting: SortConfig;
  if (bookmarkParams.sortField) {
    // convert the old sortField + sortOrder params to sorting
    const flatSortConfig: FlatValueSortConfig = {
      sortBy: SortBy.Value,
      sortOrder: bookmarkParams.sortOrder === 1 ? SortOrder.Ascending : SortOrder.Descending,
      valueField: bookmarkParams.sortField,
    };

    // this just populates `sorting.colSortAttrs`
    sorting = getDefaultSortConfig({
      numSegmentColumns,
      valueSortField: bookmarkParams.sortField,
      flatSortConfig,
    });
  } else {
    sorting = getDefaultFunnelStepsSortConfig(numSegmentColumns);
  }
  return sorting;
}

const DEPRECATED_SEGMENTS = [OTHER_PROP, OVERALL_PROP];

export function sanitizeBookmarkParams(
  bookmarkParams: FunnelBookmark,
  i18n: LocalizationStore,
): Partial<FunnelBookmark> {
  function isDeprecatedSegment(selectedSegment: string) {
    return DEPRECATED_SEGMENTS.some((deprecatedSegment) => {
      // When using comparison events, the entire segment will be something like "1A Viewed Report / $overall". So
      // it's needed to do substring match for the special segment
      return selectedSegment.endsWith(deprecatedSegment);
    });
  }

  let updatedParams: Partial<FunnelBookmark> = {};

  if (bookmarkParams.selectMode) {
    updatedParams = {...updatedParams, selectMode: undefined};
  }

  if (isEmpty(bookmarkParams.sorting)) {
    if (bookmarkParams.chart_type === ChartType.Trends) {
      // For old Funnels Trends (which don't have new `sorting` param), convert them to have a default SortConfig.
      updatedParams = {
        ...updatedParams,
        // Old Funnels Trends store sortField/sortOrder even though they didn't apply. Clear them out to clean up the
        // persisted state.
        sortField: undefined,
        sortOrder: undefined,
        sorting: getDefaultFunnelTrendSortConfig(bookmarkParams, i18n),
      };
    } else if (bookmarkParams.chart_type === ChartType.Chart) {
      const sorting = convertOrDefaultFunnelStepsSortConfig(bookmarkParams, i18n);

      updatedParams = {
        ...updatedParams,
        sortField: undefined,
        sortOrder: undefined,
        sorting,
      };
    }
  }

  if (bookmarkParams.group_by?.length > 0) {
    if (bookmarkParams.savedLegend) {
      const updatedSavedLegend = bookmarkParams.savedLegend.map((section) => ({
        selected: section.selected?.filter(({key}) => !isDeprecatedSegment(key)),
      }));
      if (!isEqual(updatedSavedLegend, bookmarkParams.savedLegend)) {
        updatedParams.savedLegend = updatedSavedLegend;
      }
    }
    if (bookmarkParams.selectedSegments) {
      const updatedSelectedSegments = bookmarkParams.selectedSegments.filter(({key}) => !isDeprecatedSegment(key));
      if (!isEqual(updatedSelectedSegments, bookmarkParams.selectedSegments)) {
        updatedParams.selectedSegments = updatedSelectedSegments;
      }
    }
  }

  const selectedSegments = updatedParams.selectedSegments ?? bookmarkParams.selectedSegments;
  if (!selectedSegments?.length && bookmarkParams.chart_type === ChartType.Trends && !bookmarkParams.group_by?.length) {
    // Old Funnel Trends bookmarks didn't persist `selectedSegments` for unsegmented queries. By modifying the bookmark
    // to include $overall, we prevent bookmark change detection issues with relying on SelectMode.Position to add
    // $overall to the `selectedSegments`
    updatedParams = {
      ...updatedParams,
      savedLegend: [{selected: [{key: OVERALL_PROP}]}],
      selectedSegments: [{color: getChartSegmentColorCombo(0).primary, key: OVERALL_PROP}],
    };
  }

  return updatedParams;
}

export function getSegmentDisplayName(segment: Segment, i18n: LocalizationStore): string {
  if (isCohortSegment(segment)) {
    const cohortSegment = segment[0];
    return propertiesUtils.renameProperty(cohortSegment[ResourceType.Cohort].name, ResourceType.Cohort, i18n);
  }
  const firstSegment = getFirstSegment(segment);
  if (isEventSegment(segment)) {
    return propertiesUtils.renameProperty(firstSegment.propertyName, ResourceType.Event, i18n, {
      dataGroupId: firstSegment.dataGroupId,
      customProperty: firstSegment.customProperty,
      propertyObjectKey: firstSegment.propertyObjectKey,
      listItemGroup: firstSegment.listItemGroup,
    });
  } else if (isUserSegment(segment)) {
    return propertiesUtils.renameProperty(firstSegment.propertyName, ResourceType.User, i18n, {
      dataGroupId: firstSegment.dataGroupId,
      customProperty: firstSegment.customProperty,
      propertyObjectKey: firstSegment.propertyObjectKey,
      listItemGroup: firstSegment.listItemGroup,
    });
  }
  const otherSegment = firstSegment as OtherSegmentation;
  return propertiesUtils.renameProperty(
    otherSegment.label?.toString() ?? otherSegment.propertyName,
    ResourceType.Other,
    i18n,
    {
      dataGroupId: firstSegment.dataGroupId,
      customProperty: firstSegment.customProperty,
      propertyObjectKey: firstSegment.propertyObjectKey,
      listItemGroup: firstSegment.listItemGroup,
    },
  );
}

export function getSegmentColumnSpecs(bookmark: FunnelBookmark, i18n: LocalizationStore): SegmentColumnSpec[] {
  let segmentColumns: SegmentColumnSpec[] = [];

  bookmark.steps.forEach((el, idx) => {
    if (isOrStep(el)) {
      segmentColumns.push({
        title: `Step ` + (idx + 1) + ` Comparison`,
        segmentType: PropertyType.String,
      });
    }
  });

  if (bookmark.group_by) {
    segmentColumns = segmentColumns.concat(
      bookmark.group_by.map((segment) => {
        return {
          title: getSegmentDisplayName(segment, i18n),
          segmentType: getSegmentPropertyType(segment),
        };
      }),
    );
  }

  return segmentColumns;
}

export function isAnyOrderFunnel(funnelOrder: FunnelOrder) {
  return funnelOrder === FunnelOrder.Any;
}

/**
 * Use FunnelSegment to get step info to create an empty Array of FunnelSteps
 */
export function createBlankSteps(steps: FunnelSegment[`steps`]): Array<BasicFunnelStep | FunnelStep> {
  return steps.map((step) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {custom_event, custom_event_id, event, goal, index, step_label} = step;
    const blankPValue = {insufficient_samples: true, p: 0, confidence: 0, is_interesting: false};
    return {
      avg_time: null,
      avg_time_from_start: null,
      count: 0,
      overall_conv_ratio: 0,
      step_conv_ratio: 0,
      p_value: blankPValue,
      custom_event,
      custom_event_id,
      event,
      goal,
      index,
      step_label,
    };
  });
}

export function getDataGroupName(
  {plural, dataGroup}: {plural: boolean; dataGroup?: DataGroup} = {plural: true, dataGroup: undefined},
) {
  return pluralize(dataGroup?.displayName || `user`, plural);
}
